import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronRight, Phone, Mail, ArrowLeft, Filter } from 'lucide-react';
import axios from 'axios';

const AT1S_LOGO = '/at1s-logo-solid.png';
const AT1S_FILTER_IMAGE = '/at1s-filter.png';

const ProductCatalog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allSizes, setAllSizes] = useState({});
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/sizes`)
      .then(res => {
        const data = res.data;
        if (data && Object.keys(data).length > 0) {
          setAllSizes(data);
          setTabs(Object.keys(data).sort((a, b) => Number(a) - Number(b)));
        }
      })
      .catch(() => {});
  }, []);

  const handleSizeClick = (size) => {
    if (user) {
      navigate(`/customer/new-order?size=${size}`);
    } else {
      navigate(`/login?redirect=/customer/new-order?size=${size}`);
    }
  };

  // Calculate image scale based on filter dimensions (larger filters = bigger image)
  const getFilterScale = (sizeStr) => {
    const parts = sizeStr.split('X').map(Number);
    const w = parts[0] || 12, h = parts[1] || 12;
    const area = w * h;
    const minArea = 100, maxArea = 750;
    const pct = 75 + ((area - minArea) / (maxArea - minArea)) * 25;
    return Math.min(100, Math.max(75, Math.round(pct)));
  };

  const getDisplaySizes = () => {
    if (activeTab === 'all') {
      const all = [];
      tabs.forEach(t => {
        (allSizes[t] || []).forEach(size => {
          all.push({ size, thickness: t });
        });
      });
      return all;
    }
    return (allSizes[activeTab] || []).map(size => ({ size, thickness: activeTab }));
  };

  const displaySizes = getDisplaySizes();

  return (
    <div className="min-h-screen bg-white" data-testid="product-catalog-page">
      {/* Header */}
      <header className="bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img src={AT1S_LOGO} alt="AirTech OneSolutions" className="h-10 w-auto object-contain" />
            </div>
            <div className="flex items-center gap-3">
              {user ? (
                <button onClick={() => navigate(user.role === 'admin' ? '/dashboard' : '/customer/dashboard')} className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="my-account-btn">
                  My Account
                </button>
              ) : (
                <button onClick={() => navigate('/login')} className="text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="sign-in-btn">
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Page Title */}
      <section className="bg-[#0a1628] pb-10 pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="catalog-title">
            Product Catalog
          </h1>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Professional-grade MERV-rated pleated air filters in every size. Click any filter to order.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3 overflow-x-auto" data-testid="catalog-filter-bar">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'all' ? 'bg-[#0a1628] text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="filter-all"
            >
              All Sizes ({Object.values(allSizes).reduce((a, v) => a + v.length, 0)})
            </button>
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab ? 'bg-[#0a1628] text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${tab}-inch`}
              >
                {tab}" Thick ({allSizes[tab]?.length || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {displaySizes.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="catalog-grid">
              {displaySizes.map(({ size, thickness }) => {
                const scale = getFilterScale(size);
                return (
                  <button
                    key={`${size}-${thickness}`}
                    onClick={() => handleSizeClick(size)}
                    className="group relative bg-white rounded-xl border border-gray-200 hover:border-teal-400 hover:shadow-lg transition-all overflow-hidden text-left"
                    data-testid={`catalog-card-${size}`}
                  >
                    {/* Filter Image */}
                    <div className="relative aspect-square overflow-hidden flex items-center justify-center">
                      <img
                        src={AT1S_FILTER_IMAGE}
                        alt={`AT1S Air Filter ${size}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {/* Thickness Badge */}
                      <div className="absolute bottom-2 left-2 bg-teal-600/90 text-white text-[10px] font-semibold px-2 py-1 rounded-md backdrop-blur-sm z-10">
                        {thickness}" Thick
                      </div>
                    </div>
                    {/* Card Footer */}
                    <div className="px-3 py-2.5 border-t border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-700 transition-colors">{size}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">MERV 8 / 11 / 13</span>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-teal-500 transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-400">Loading catalog...</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#060e1a] text-white/60 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <img src={AT1S_LOGO} alt="AirTech OneSolutions" className="h-10 w-auto object-contain mb-3 mx-auto md:mx-0" />
              <p className="text-sm">Professional-grade residential and commercial disposable filters.</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2 text-sm">
              <a href="mailto:contactat1s@air-techsolutions.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" /> contactat1s@air-techsolutions.com
              </a>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> +1 832 835 7362 | +1 832 228 7463
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-6 pt-6 text-center text-xs text-white/40">
            &copy; {new Date().getFullYear()} AirTech OneSolutions. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductCatalog;
